import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../shared/components/Modal';
import { MediaUploader, type AssetEntry } from '../../../shared/components/MediaUploader';
import { IntakeFieldRenderer, validateIntake } from '../../../shared/components/IntakeFieldRenderer';
import { useAuthStore } from '../../auth/store';
import { useMerchant } from '../../merchants/hooks/useMerchants';
import { fieldConfigApi } from '../../field-config/api/fieldConfigApi';
import { useCreateCustomOrder, useGuardrails } from '../hooks/useCustomOrders';
import { buildCreateCustomOrderPayload } from '../payload';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_ASSETS = 10;
const field = 'w-full rounded-md border border-border px-3 py-2 text-sm focus:border-gray-500 focus:outline-none';

const mb = (bytes: number | undefined) => (bytes ? `${Math.round(bytes / (1024 * 1024))}MB` : null);

/**
 * PE-2285 — admin "new custom order" with inspiration assets.
 *
 * Sibling of orders/NewIntakeOrderModal (the schema-driven "create on behalf of a
 * customer" form): same field-config-driven intake, plus the asset upload the review
 * screen's "Assets (n)" panel was always reading but nothing in Osprey could write.
 *
 * Two-step by contract: `POST /media/upload` takes the bytes (multipart, via
 * MediaUploader → useMediaUpload), then `POST /custom-orders` takes JSON refs
 * ({ objectKey, bucket, mimeType, assetType }) — see payload.ts, which also documents
 * the osprey-api `x-access` forwarding gap that leaves these objects in the public bucket.
 */
export function NewCustomOrderModal({ isOpen, onClose }: Props) {
  const merchantId = useAuthStore((s) => s.selectedTenantId);
  const { data: merchant } = useMerchant(merchantId ?? '');
  const solutionId = merchant?.solutionId ?? '';

  const [customerEmail, setCustomerEmail] = useState('');
  const [intake, setIntake] = useState<Record<string, unknown>>({});
  const [assets, setAssets] = useState<AssetEntry[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createdId, setCreatedId] = useState<string | null>(null);

  const ready = !!merchantId && !!solutionId;

  // The intake object is validated upstream against the merchant's CUSTOM_ORDER
  // catalogue, so the form must be built from it rather than hand-rolled.
  const schemaQ = useQuery({
    queryKey: ['field-config', merchantId, 'CUSTOM_ORDER'],
    queryFn: () => fieldConfigApi.getMerchantConfig(merchantId as string, 'CUSTOM_ORDER'),
    enabled: isOpen && !!merchantId,
  });
  const fields = useMemo(() => schemaQ.data?.effective ?? [], [schemaQ.data]);

  const { data: guardrails } = useGuardrails(isOpen ? merchantId : null);
  const createMutation = useCreateCustomOrder();

  const uploading = assets.some((a) => !a.objectKey);

  const reset = () => {
    setCustomerEmail('');
    setIntake({});
    setAssets([]);
    setErrors({});
    setCreatedId(null);
  };

  const close = () => {
    onClose();
    reset();
  };

  const submit = () => {
    const e: Record<string, string> = {};
    if (!customerEmail.trim()) e.customerEmail = 'Customer email is required';
    Object.assign(e, validateIntake(fields, intake));
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    createMutation.mutate(
      buildCreateCustomOrderPayload({ customerEmail, solutionId, intake, assets }),
      { onSuccess: (order) => setCreatedId(order?.id ?? '') },
    );
  };

  const limits = [
    guardrails && `images ${mb(guardrails.imageMaxBytes)}`,
    guardrails && `videos ${mb(guardrails.videoMaxBytes)}`,
  ].filter(Boolean).join(' · ');

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <div className="p-6">
        <h2 className="mb-1 text-lg font-semibold text-foreground">New custom order</h2>
        <p className="mb-5 text-sm text-muted-foreground">
          {merchant ? `Merchant: ${merchant.name ?? merchantId}` : 'Select a merchant to begin.'}
        </p>

        {!merchantId && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">Select a merchant first.</p>
        )}
        {merchantId && merchant && !solutionId && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            This merchant’s profile is missing its solution assignment. Assign a solution before creating orders on their behalf.
          </p>
        )}

        {createdId !== null ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-foreground">
              The custom order was created for {customerEmail}
              {assets.length > 0 ? ` with ${assets.length} asset${assets.length > 1 ? 's' : ''}` : ''}. It
              has no price yet, so there is no payment link to share.
            </p>
            <ol className="ml-4 list-decimal text-sm text-muted-foreground">
              <li>Open it from this list and set a price.</li>
              <li>Mark it ready — this issues the invoice and notifies the customer.</li>
              <li>Copy the payment link from the order to share it directly.</li>
            </ol>
            <div className="flex gap-2">
              <button onClick={close} className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white">Done</button>
              <button onClick={reset} className="rounded-md border border-border px-4 py-2 text-sm">Create another</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                Customer email <span className="text-red-500">*</span>
              </label>
              <input type="email" className={field} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
              {errors.customerEmail && <p className="mt-1 text-xs text-red-600">{errors.customerEmail}</p>}
            </div>

            {schemaQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading the merchant’s intake fields…</p>
            ) : fields.length > 0 ? (
              <div className="border-t border-border pt-4">
                <IntakeFieldRenderer
                  fields={fields}
                  values={intake}
                  errors={errors}
                  onChange={(k, v) => setIntake((p) => ({ ...p, [k]: v }))}
                />
              </div>
            ) : null}

            <div className="border-t border-border pt-4">
              <label className="mb-1 block text-sm font-medium text-foreground">Assets</label>
              <p className="mb-2 text-xs text-muted-foreground">
                Inspiration images or videos supplied by the customer. These are what the order
                review screen lists under “Assets”.{limits ? ` Upload limits: ${limits}.` : ''}
              </p>
              <MediaUploader assets={assets} onChange={setAssets} maxAssets={MAX_ASSETS} solutionId={solutionId || 'rack-and-box'} />
            </div>

            <div className="mt-2 flex justify-end gap-2">
              <button onClick={close} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
              <button
                onClick={submit}
                disabled={!ready || uploading || createMutation.isPending}
                className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating…' : uploading ? 'Uploading…' : 'Create order'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
